import {
  Box,
  Typography,
  Grid,
  Skeleton,
  Button,
  alpha,
  Avatar,
  Collapse,
} from '@mui/material';
import React, { useEffect, useState, startTransition, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Add,
  AccountTree,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { ProjectStatusEnum, UserRole, Project, Site } from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';
import { ProjectWithSites } from 'src/state-management/state';
import { useSnackbar } from 'src/view/components/SnackbarService';
import FullPageMessage from 'src/view/components/FullPageMessage';
import ProjectDialog from 'src/view/components/ProjectDialog';
import Header from 'src/view/components/navigation/Header';
import DownloadAppDialog from 'src/view/components/DownloadAppDialog';
import { formatDate } from 'src/util/date-utils';
import AdminOverview from 'src/view/Admin/AdminOverview';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { mapBoxConfig } from 'src/mapbox-helpers/config';
import { addSiteBoundaryWithColor } from 'src/view/InteractiveMap/map-helper';
import { getColorByIndex } from 'src/mapbox-helpers/color-utils';

import './style.scss';

// --- Sub-components ---

const PlayStoreIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.5 2.1C3.3 2.1 3.1 2.2 3 2.4L13.1 12.5L21 20.4L21.1 20.5C21.4 20.2 21.6 19.8 21.6 19.2V4.8C21.6 4.2 21.4 3.8 21.1 3.5L3.5 2.1Z" fill="#EA4335" />
    <path d="M21.1 3.5C21.1 3.5 21.1 3.5 21 3.5L3.5 2.1C3.1 2.1 2.8 2.2 2.6 2.5L12.5 12.4L21.1 3.5Z" fill="#FBBC04" />
    <path d="M3 21.6C3.1 21.8 3.3 21.9 3.5 21.9L21 20.5C21.1 20.5 21.1 20.5 21.1 20.5L12.5 11.9L3 21.6Z" fill="#34A853" />
    <path d="M2.6 2.5C2.4 2.7 2.3 3 2.3 3.5V20.5C2.3 21 2.4 21.3 2.6 21.5L13.1 11L2.6 2.5Z" fill="#4285F4" />
  </svg>
);

const parseWKT = (wkt: string | null | undefined): [number, number] | null => {
  if (!wkt) return null;
  const match = wkt.match(/POINT\s*\(([^)]+)\)/i);
  if (match) {
    const coords = match[1].trim().split(/\s+/);
    if (coords.length === 2) {
      const lng = parseFloat(coords[0]);
      const lat = parseFloat(coords[1]);
      if (!isNaN(lng) && !isNaN(lat)) {
        return [lng, lat];
      }
    }
  }
  return null;
};

const LandingMap: React.FC<{ projects: Project[] }> = ({ projects }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!mapContainer.current) return;

    mapboxgl.accessToken = mapBoxConfig.accessToken;
    const m = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/satellite-v9',
      center: [78.9629, 20.5937],
      zoom: 4,
      attributionControl: false
    });
    map.current = m;

    m.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const activePopups: mapboxgl.Popup[] = [];
    const projectMarkers: mapboxgl.Marker[] = [];
    const siteMarkers: mapboxgl.Marker[] = [];

    const hoverPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'site-hover-popup'
    });

    const updateMarkerVisibility = () => {
      const currentZoom = m.getZoom();
      siteMarkers.forEach(marker => {
        const el = marker.getElement();
        el.style.display = currentZoom >= 6.0 ? 'block' : 'none';
      });
    };

    // Everything runs after the map style is fully loaded
    m.on('load', () => {
      m.setFog({
        color: 'rgb(186, 210, 235)',
        'high-color': 'rgb(36, 92, 223)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(11, 11, 25)',
        'star-intensity': 0.6
      });

      projects.forEach(project => {
        const projectWithSites = project as ProjectWithSites;
        const projectSites = projectWithSites.sites || [];

        if (projectSites.length === 0) return;

        const isSampleProject = project.project_id === process.env.REACT_APP_SAMPLE_PROJECT_ID;

        // ── Site boundaries (same pattern as InteractiveMap) ──────────────
        if (!isSampleProject) projectSites.forEach((site, index) => {
          if (!site.boundary) return;
          try {
            addSiteBoundaryWithColor(m, site, getColorByIndex(index), {
              onMouseEnter: (e) => {
                m.getCanvas().style.cursor = 'pointer';
                if (e.features && e.features.length > 0) {
                  const props = e.features[0].properties;
                  hoverPopup
                    .setLngLat(e.lngLat)
                    .setHTML(`
                      <div style="color:#fff;font-size:11px;">
                        <strong>${props?.Name}</strong><br/>
                        Area: ${props?.Area} ha
                      </div>
                    `)
                    .addTo(m);
                }
              },
              onMouseLeave: () => {
                m.getCanvas().style.cursor = '';
                hoverPopup.remove();
              }
            });
          } catch (err) {
            console.error('Error rendering site boundary', err);
          }
        });

        // ── Project centroid marker ───────────────────────────────────────
        let totalLng = 0, totalLat = 0, count = 0;
        projectSites.forEach((site: Site) => {
          const coords = parseWKT(site.centroid_wkt);
          if (coords) { totalLng += coords[0]; totalLat += coords[1]; count++; }
        });
        if (count === 0) return;

        const projectCoords: [number, number] = [totalLng / count, totalLat / count];

        const closeAllPopups = () => {
          activePopups.forEach(p => p.remove());
        };

        if (!isSampleProject) {
          const el = document.createElement('div');
          el.className = 'project-marker';
          el.innerHTML = `
            <svg width="32" height="32" viewBox="0 0 24 24" fill="#EF4444" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              <circle cx="12" cy="9" r="2.5" fill="#FFF"/>
            </svg>
          `;
          el.style.cursor = 'pointer';
          el.style.filter = 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))';

          const formattedStartDate = formatDate(project.project_start_date);
          const formattedEndDate = formatDate(project.project_end_date);

          const popupHtml = `
            <div class="project-popup-card">
              <div class="popup-card-header">
                <span class="popup-status-badge">UNDER DEVELOPMENT</span>
              </div>
              <div class="popup-card-content">
                <h3 class="popup-project-name">${project.project_name}</h3>
                <div class="popup-meta-row">
                  <span class="popup-meta-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                  </span>
                  <span class="popup-meta-text">${project.project_type || 'Nature Research Society'}</span>
                </div>
                <div class="popup-meta-row">
                  <span class="popup-meta-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path><path d="M2 12h20"></path></svg>
                  </span>
                  <span class="popup-meta-text">${project.country || 'India'}</span>
                </div>
              </div>
              <hr class="popup-divider" />
              <div class="popup-card-footer">
                <div class="popup-stat-col">
                  <span class="popup-stat-label">Sites</span>
                  <span class="popup-stat-value">${projectSites.length}</span>
                </div>
                <div class="popup-stat-col">
                  <span class="popup-stat-label">Start Date</span>
                  <span class="popup-stat-value">${formattedStartDate || '-'}</span>
                </div>
                <div class="popup-stat-col">
                  <span class="popup-stat-label">End Date</span>
                  <span class="popup-stat-value">${formattedEndDate || '-'}</span>
                </div>
                <button id="popup-btn-${project.project_id}" class="popup-play-btn" aria-label="Enter Project">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
              </div>
            </div>
          `;

          const popup = new mapboxgl.Popup({ offset: 15, className: 'custom-popup', maxWidth: 'none' })
            .setHTML(popupHtml);

          popup.on('open', () => {
            const btn = document.getElementById(`popup-btn-${project.project_id}`);
            if (btn) {
              btn.addEventListener('click', () => navigate(`/project/${project.project_id}/overview`));
            }
          });

          activePopups.push(popup);

          el.addEventListener('click', (e) => {
            e.stopPropagation();

            // If popup is already open, close it and return
            if (popup.isOpen()) {
              popup.remove();
              return;
            }

            // Close other popups before flying/opening
            closeAllPopups();

            // Fly to the project centroid, then open the popup once the animation settles
            m.flyTo({ center: projectCoords, zoom: 7.5, essential: true, duration: 1500 });

            const onMoveEnd = () => {
              m.off('moveend', onMoveEnd);
              popup.setLngLat(projectCoords).addTo(m);
            };

            m.once('moveend', onMoveEnd);
          });

          const marker = new mapboxgl.Marker(el, { anchor: 'bottom' })
            .setLngLat(projectCoords)
            .setPopup(popup)
            .addTo(m);
          projectMarkers.push(marker);
        }

        // ── Individual site name labels (visible at zoom >= 6) ────────────
        if (!isSampleProject) projectSites.forEach(site => {
          const siteCoords = parseWKT(site.centroid_wkt);
          if (siteCoords) {
            const siteEl = document.createElement('div');
            siteEl.className = 'site-marker';
            siteEl.style.display = 'none'; // hidden until zoom >= 6
            siteEl.style.cursor = 'pointer';
            siteEl.innerHTML = `
              <div class="site-marker-pin"></div>
              <div class="site-marker-label">${site.site_name}</div>
            `;

            const sitePopupHtml = `
              <div class="project-popup-card">
                <div class="popup-card-header">
                  <span class="popup-status-badge" style="background:rgba(16,185,129,0.15);color:#10B981;">SITE</span>
                </div>
                <div class="popup-card-content">
                  <h3 class="popup-project-name">${site.site_name}</h3>
                  <div class="popup-meta-row">
                    <span class="popup-meta-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                    </span>
                    <span class="popup-meta-text">${project.project_name}</span>
                  </div>
                </div>
                <hr class="popup-divider" />
                <div class="popup-card-footer">
                  <div class="popup-stat-col">
                    <span class="popup-stat-label">Area</span>
                    <span class="popup-stat-value">${site.area ? `${site.area} ha` : '-'}</span>
                  </div>
                  <div class="popup-stat-col">
                    <span class="popup-stat-label">Site ID</span>
                    <span class="popup-stat-value" style="font-size:10px;">${site.site_id.slice(0, 8)}…</span>
                  </div>
                  <button id="site-popup-btn-${site.site_id}" class="popup-play-btn" aria-label="Go to Site">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                  </button>
                </div>
              </div>
            `;

            const sitePopup = new mapboxgl.Popup({
              offset: 10,
              className: 'custom-popup',
              closeButton: true,
              closeOnClick: false,
              maxWidth: 'none'
            }).setHTML(sitePopupHtml);

            sitePopup.on('open', () => {
              const btn = document.getElementById(`site-popup-btn-${site.site_id}`);
              if (btn) {
                btn.addEventListener('click', () => {
                  navigate(`/project/${project.project_id}/overview`);
                });
              }
            });

            activePopups.push(sitePopup);

            siteEl.addEventListener('click', (e) => {
              e.stopPropagation();

              if (sitePopup.isOpen()) {
                sitePopup.remove();
                return;
              }

              // Close all other popups
              closeAllPopups();

              sitePopup.setLngLat(siteCoords).addTo(m);
            });

            const sMarker = new mapboxgl.Marker(siteEl, { anchor: 'center' })
              .setLngLat(siteCoords)
              .addTo(m);
            siteMarkers.push(sMarker);
          }
        });
      });

      // Set initial visibility after all markers are added
      updateMarkerVisibility();
    });

    m.on('zoom', updateMarkerVisibility);

    return () => {
      m.off('zoom', updateMarkerVisibility);
      hoverPopup.remove();
      activePopups.forEach(p => p.remove());
      projectMarkers.forEach(mk => mk.remove());
      siteMarkers.forEach(mk => mk.remove());
      m.remove();
    };
  }, [projects, navigate]);

  return <Box ref={mapContainer} className="map-section" />;
};

const Sidebar: React.FC<{ projects: Project[], onNewProject: () => void }> = ({ projects, onNewProject }) => {
  const sampleProjectId = process.env.REACT_APP_SAMPLE_PROJECT_ID;
  const sampleProject = projects.find(p => p.project_id === sampleProjectId);
  const realProjects = projects.filter(p => p.project_id !== sampleProjectId);

  const ongoingProjects = realProjects.filter(p => p.project_status === ProjectStatusEnum.UnderDevelopment);
  const plannedProjects = realProjects.filter(p => p.project_status === ProjectStatusEnum.Draft);

  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  
  const toggleCategory = (cat: string) => {
    setExpandedCategory(expandedCategory === cat ? null : cat);
  };

  const recentProjects = realProjects.slice(0, 3);
  const navigate = useNavigate();
  const { state } = useAppState();
  
  const userRoles = state.user?.role || [];
  const hideNewProjectBtn = userRoles.includes(UserRole.Employee) || userRoles.includes(UserRole.Dev) || userRoles.includes(UserRole.FieldWorker);

  const renderStatItem = (title: string, categoryProjects: Project[]) => {
    const isExpanded = expandedCategory === title;
    return (
      <Box sx={{ mb: '8px' }} key={title}>
        <Box 
          className="sidebar-stat-item" 
          onClick={() => toggleCategory(title)}
          sx={{ cursor: 'pointer', mb: 0 }}
        >
          <Typography>{title}</Typography>
          <Typography className="stat-value">{categoryProjects.length}</Typography>
        </Box>
        <Collapse in={isExpanded}>
          <Box sx={{ pl: 3, pr: 1, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {categoryProjects.length === 0 && (
              <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', fontStyle: 'italic', pl: 1 }}>
                No projects
              </Typography>
            )}
            {categoryProjects.map(p => (
              <Box 
                key={p.project_id} 
                className="recent-project-item" 
                onClick={() => navigate(`/project/${p.project_id}/overview`)}
                sx={{ mb: 0, p: '6px' }}
              >
                <Avatar variant="rounded" sx={{ width: 24, height: 24, bgcolor: alpha('#10B981', 0.1), color: '#10B981' }}>
                  <AccountTree sx={{ fontSize: 14 }} />
                </Avatar>
                <Box className="project-info">
                  <Typography className="project-name" sx={{ fontSize: '13px' }}>{p.project_name}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box className="dashboard-sidebar">
      <Box className="sidebar-section">
        {!hideNewProjectBtn && (
          <Button
            fullWidth
            variant="contained"
            startIcon={<Add />}
            onClick={onNewProject}
            className="new-project-sidebar-btn"
            sx={{ bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' } }}
          >
            New Project
          </Button>
        )}
        {sampleProject && (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate(`/project/${sampleProject.project_id}/overview`)}
            sx={{ 
              mt: 2, 
              color: '#10B981', 
              borderColor: '#10B981', 
              '&:hover': { bgcolor: 'rgba(16,185,129,0.1)', borderColor: '#10B981' } 
            }}
          >
            Try Sample Project
          </Button>
        )}
      </Box>

      <Box className="sidebar-section">
        <Typography className="sidebar-title" sx={{ mb: 2 }}>Overview</Typography>
        {renderStatItem('All Projects', realProjects)}
        {renderStatItem('Ongoing', ongoingProjects)}
        {renderStatItem('Planned', plannedProjects)}
      </Box>

      <Box className="sidebar-section" sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography className="sidebar-title" sx={{ mb: 0 }}>Recent Projects</Typography>
          <Box sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: '#10B981',
            boxShadow: '0 0 10px #10B981, 0 0 20px rgba(16,185,129,0.4)',
            animation: 'pulse-glow 2s infinite'
          }} />
        </Box>
        {recentProjects.map(p => (
          <Box key={p.project_id} className="recent-project-item" onClick={() => navigate(`/project/${p.project_id}/overview`)}>
            <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: alpha('#10B981', 0.1), color: '#10B981' }}>
              <AccountTree sx={{ fontSize: 18 }} />
            </Avatar>
            <Box className="project-info">
              <Typography className="project-name">{p.project_name}</Typography>
              <Typography className="project-meta">{p.project_type || 'Nature based'}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// --- Main Page ---

// --- Main Page ---

const LandingPage: React.FC = () => {
  const { showSnackbar, snackbarType } = useSnackbar();
  const { state, dispatch } = useAppState();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDownloadAppDialogOpen, setIsDownloadAppDialogOpen] = useState(false);

  // Home is outside every project; drop the last one so tools opened from here don't inherit it.
  useEffect(() => {
    dispatch({ type: 'SET_PROJECT_DETAILS', payload: undefined });
  }, [dispatch]);

  useEffect(() => {
    const fetchProjectsAndSites = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // 1. Fetch Projects
        const projectsResponse = await clientService.api.readProjectsApiV1ProjectsGet();
        const projects = projectsResponse.data || [];
        
        // 2. Fetch Sites for each project
        const projectsWithSites = await Promise.all(projects.map(async (project) => {
          try {
            const sitesResponse = await clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(project.project_id);
            return {
              ...project,
              sites: sitesResponse.data || []
            };
          } catch (siteError) {
            console.error(`Failed to fetch sites for project ${project.project_id}:`, siteError);
            return { ...project, sites: [] };
          }
        }));

        startTransition(() => {
          dispatch({ type: 'SET_PROJECT_LIST', payload: projectsWithSites });
          setIsLoading(false);
        });
      } catch (apiError: unknown) {
        const errorMessage =
          (apiError as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
          'Failed to load dashboard data.';
        setError(errorMessage);
        showSnackbar({ message: errorMessage, type: snackbarType.ERROR });
        setIsLoading(false);
      }
    };
    fetchProjectsAndSites();
  }, [dispatch, showSnackbar, snackbarType]);

  const handleDialogOpen = useCallback(() => setIsDialogOpen(true), []);
  const handleDialogClose = useCallback(() => setIsDialogOpen(false), []);

  if (isLoading) {
    return (
      <Box className="landing-page-container">
        <Header />
        <Box className="dashboard-wrapper">
          <Skeleton variant="rectangular" width={280} height="100%" sx={{ bgcolor: '#111' }} />
          <Box sx={{ flex: 1, p: 4 }}>
            <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2, mb: 4, bgcolor: '#111' }} />
            <Grid container spacing={4}>
              {[...Array(3)].map((_, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2, bgcolor: '#111' }} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) return <FullPageMessage headline="Error" subHeadline={error} />;

  // Admin view
  if (state.user?.role.includes(UserRole.Admin)) {
    return (
      <Box className="landing-page-container">
        <Header />
        <Box sx={{ flexGrow: 1, pt: '73px' }}>
          <AdminOverview />
        </Box>
      </Box>
    );
  }

  return (
    <Box className="landing-page-container">
      <Header />
      <Box className="dashboard-wrapper">
        <Sidebar projects={state.projects} onNewProject={handleDialogOpen} />

        <Box className="dashboard-main">
          {/* App Download Strip */}
          <Box className="download-strip">
            <Button
              className="play-store-btn"
              startIcon={<PlayStoreIcon />}
              onClick={() => setIsDownloadAppDialogOpen(true)}
            >
              Download App
            </Button>
          </Box>

          {/* Map Section */}
          <LandingMap projects={state.projects} />
        </Box>
      </Box>

      <ProjectDialog open={isDialogOpen} onClose={handleDialogClose} />
      <DownloadAppDialog open={isDownloadAppDialogOpen} onClose={() => setIsDownloadAppDialogOpen(false)} />
    </Box>
  );
};

export default LandingPage;
